import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plane, FileText, Camera, Users,
  TrendingUp, AlertTriangle, Calendar, Plus
} from 'lucide-react';
import { apiGet } from './appApi';
import { formatDateShort, caymanDateKey } from '../../utils/dateFormat';
import { logger } from '../../utils/logger';
import { usePullToRefresh, PullToRefreshIndicator } from '../../hooks/usePullToRefresh';
import { SkeletonStatCard, SkeletonListRow } from '../../components/app/Skeleton';

// Vibrant quick-action card
const QuickAction = ({ to, icon: Icon, label, color, onClick }) => (
  <button
    onClick={onClick}
    data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}
    className={`flex flex-col items-center justify-center gap-1.5 py-4 px-2 rounded-xl ${color} text-white active:scale-95 transition`}
  >
    <Icon className="w-6 h-6" />
    <span className="text-[12px] font-semibold leading-tight text-center">{label}</span>
  </button>
);

const StatCard = ({ label, value, hint, tone = 'default' }) => {
  const tones = {
    default: 'bg-white',
    positive: 'bg-emerald-50 border-emerald-200',
    warning: 'bg-amber-50 border-amber-200',
  };
  return (
    <div className={`${tones[tone]} border border-gray-200 rounded-xl p-3`}>
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-900 mt-1 leading-none">{value}</p>
      {hint && <p className="text-[11px] text-gray-500 mt-1">{hint}</p>}
    </div>
  );
};

export const AppHome = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [upcomingTravels, setUpcomingTravels] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [offline, setOffline] = useState(!navigator.onLine);

  const load = useCallback(async () => {
      try {
        const [statsRes, relocations] = await Promise.all([
          apiGet('/api/admin/stats'),
          apiGet('/api/admin/relocations'),
        ]);
        setStats(statsRes);
        setOffline(false);

        const todayKey = caymanDateKey(new Date());
        const [ty, tm, td] = todayKey.split('-').map(Number);
        const todayUTC = Date.UTC(ty, tm - 1, td);

        const upcoming = [];
        const alertList = [];

        relocations.forEach((r) => {
          if (!r.travel_date) return;
          const key = caymanDateKey(r.travel_date);
          const [y, m, d] = key.split('-').map(Number);
          const travelUTC = Date.UTC(y, m - 1, d);
          const days = Math.round((travelUTC - todayUTC) / (1000 * 60 * 60 * 24));

          if (days >= 0 && days <= 60) {
            upcoming.push({ ...r, days });
          }
          if (days > 0 && days <= 7) {
            alertList.push({
              id: r.id,
              message: `${r.pet_name} travels in ${days} day${days === 1 ? '' : 's'}`,
              days,
            });
          }
        });

        upcoming.sort((a, b) => a.days - b.days);
        setUpcomingTravels(upcoming.slice(0, 5));
        setAlerts(alertList);
      } catch (err) {
        logger.error('Dashboard load error:', err);
        setOffline(true);
      }
  }, []);

  useEffect(() => {
    load().finally(() => setLoading(false));
    const on = () => setOffline(false);
    const off = () => setOffline(true);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, [load]);

  const { containerRef, pullDistance, isRefreshing } = usePullToRefresh(load);

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 18 ? 'Good afternoon' : 'Good evening';

  return (
    <div ref={containerRef}>
      {/* Header */}
      <div
        className="bg-gradient-to-br from-teal-600 to-cyan-700 text-white pb-6 px-5 rounded-b-3xl"
        style={{ paddingTop: 'calc(env(safe-area-inset-top) + 24px)' }}
      >
        <p className="text-sm text-teal-100">{greeting}</p>
        <h1 className="text-2xl font-bold mt-0.5">Dr. Samantha</h1>
        <p className="text-xs text-teal-100 mt-1">
          {now.toLocaleDateString('en-GB', {
            weekday: 'long', day: 'numeric', month: 'long',
            timeZone: 'America/Cayman',
          })}
        </p>
        {offline && (
          <p className="mt-2 inline-block text-[11px] bg-amber-500/20 border border-amber-200/50 text-amber-50 rounded-lg px-2 py-1" data-testid="home-offline-banner">
            Offline — showing cached data
          </p>
        )}
      </div>

      <PullToRefreshIndicator distance={pullDistance} refreshing={isRefreshing} />

      <div className="px-4 -mt-4 space-y-4">
        {/* Alerts banner */}
        {alerts.length > 0 && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 shadow-sm" data-testid="home-alerts">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-semibold text-amber-900">
                  {alerts.length} urgent {alerts.length === 1 ? 'alert' : 'alerts'}
                </p>
                <ul className="mt-1 space-y-0.5">
                  {alerts.slice(0, 3).map((a) => (
                    <li key={a.id} className="text-xs text-amber-800">• {a.message}</li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Stats */}
        {loading ? (
          <div className="grid grid-cols-3 gap-2" data-testid="home-stats-loading">
            {[0, 1, 2].map((i) => <SkeletonStatCard key={i} />)}
          </div>
        ) : stats && (
          <div className="grid grid-cols-3 gap-2" data-testid="home-stats">
            <StatCard
              label="Pending"
              value={stats.pending_invoices}
              hint="invoices"
              tone={stats.pending_invoices > 0 ? 'warning' : 'default'}
            />
            <StatCard
              label="Paid YTD"
              value={stats.paid_invoices}
              hint={`CI$${(stats.total_revenue || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`}
              tone="positive"
            />
            <StatCard
              label="Overdue"
              value={stats.overdue_invoices}
              hint="follow up"
              tone={stats.overdue_invoices > 0 ? 'warning' : 'default'}
            />
          </div>
        )}

        {/* Quick Actions */}
        <div>
          <h2 className="text-sm font-semibold text-gray-700 mb-2 px-1">Quick actions</h2>
          <div className="grid grid-cols-4 gap-2">
            <QuickAction
              icon={Plus}
              label="New Invoice"
              color="bg-teal-600"
              onClick={() => navigate('/admin/invoices/new')}
            />
            <QuickAction
              icon={Camera}
              label="Scan Doc"
              color="bg-emerald-600"
              onClick={() => navigate('/app/scan')}
            />
            <QuickAction
              icon={FileText}
              label="New Quote"
              color="bg-cyan-600"
              onClick={() => navigate('/admin/quotes/new')}
            />
            <QuickAction
              icon={Plane}
              label="New Relocation"
              color="bg-sky-600"
              onClick={() => navigate('/admin/relocations/new')}
            />
          </div>
        </div>

        {/* Upcoming Travel */}
        <div>
          <div className="flex items-center justify-between mb-2 px-1">
            <h2 className="text-sm font-semibold text-gray-700">Upcoming travel</h2>
            <button
              onClick={() => navigate('/app/relocations')}
              className="text-xs text-teal-600 font-medium"
              data-testid="home-view-all-travels"
            >
              View all →
            </button>
          </div>

          {loading ? (
            <div className="space-y-2">
              {[0, 1, 2].map(i => <SkeletonListRow key={i} />)}
            </div>
          ) : upcomingTravels.length === 0 ? (
            <div className="bg-white border border-gray-200 rounded-xl p-5 text-center">
              <Calendar className="w-8 h-8 text-gray-300 mx-auto mb-2" />
              <p className="text-sm text-gray-500">No travel scheduled in the next 60 days</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="home-upcoming-travel">
              {upcomingTravels.map((r) => (
                <button
                  key={r.id}
                  onClick={() => navigate(`/admin/relocations/${r.id}`)}
                  className="w-full bg-white border border-gray-200 rounded-xl p-3 flex items-center gap-3 active:bg-gray-50 text-left"
                >
                  <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                    r.days <= 7 ? 'bg-amber-100 text-amber-600' : 'bg-teal-100 text-teal-600'
                  }`}>
                    <Plane className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-gray-900 text-sm truncate">{r.pet_name}</p>
                    <p className="text-xs text-gray-500 truncate">
                      {r.origin} → {r.destination}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <p className="text-xs font-semibold text-teal-600">{formatDateShort(r.travel_date)}</p>
                    <p className="text-[11px] text-gray-500">
                      {r.days === 0 ? 'today' : r.days === 1 ? 'tomorrow' : `in ${r.days} days`}
                    </p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Earnings hint */}
        {!loading && stats && (
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-5 h-5" />
            </div>
            <div className="flex-1">
              <p className="text-xs text-gray-500">Total revenue (paid)</p>
              <p className="text-xl font-bold text-gray-900">
                CI${(stats.total_revenue || 0).toLocaleString()}
              </p>
            </div>
            <button
              onClick={() => navigate('/admin/invoices')}
              className="text-xs text-teal-600 font-medium"
            >
              Details →
            </button>
          </div>
        )}

        {/* Clients shortcut */}
        <button
          onClick={() => navigate('/admin/clients')}
          className="w-full bg-white border border-gray-200 rounded-xl p-4 flex items-center gap-3 active:bg-gray-50"
        >
          <div className="w-10 h-10 rounded-full bg-cyan-100 text-cyan-600 flex items-center justify-center flex-shrink-0">
            <Users className="w-5 h-5" />
          </div>
          <div className="flex-1 text-left">
            <p className="font-semibold text-gray-900 text-sm">Clients</p>
            <p className="text-xs text-gray-500">Manage portal accounts</p>
          </div>
          <span className="text-teal-600">→</span>
        </button>
      </div>
    </div>
  );
};
